import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  Activity, ArrowRight, Bell, BellRing, CheckCircle2, DollarSign, 
  Eye, FileText, Gavel, Info, Loader2, Scale, Scan, Search, 
  Sparkles, Target, Upload, Users, X
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import TrialezeLogo from "@/components/shared/TrialezeLogo";
import Disclaimer from "@/components/shared/Disclaimer";
import Footer from "@/components/shared/Footer";

const HomePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showDemo, setShowDemo] = useState(false);
  const [demoStep, setDemoStep] = useState(0);
  const [demoData, setDemoData] = useState({
    caseName: '',
    plaintiff: '',
    defendant: '',
    tort: '',
    analysis: '',
    settlement: ''
  });

  // Auto-play demo animation
  useEffect(() => {
    if (!showDemo) {
      setDemoStep(0);
      setDemoData({});
      return;
    }
    
    const demoSequence = [
      // Step 1: Case Setup - Basic Info
      { delay: 500, action: () => setDemoData(d => ({ ...d, caseName: 'J' })) },
      { delay: 80, action: () => setDemoData(d => ({ ...d, caseName: 'Jo' })) },
      { delay: 80, action: () => setDemoData(d => ({ ...d, caseName: 'Joh' })) },
      { delay: 80, action: () => setDemoData(d => ({ ...d, caseName: 'John' })) },
      { delay: 80, action: () => setDemoData(d => ({ ...d, caseName: 'Johns' })) },
      { delay: 80, action: () => setDemoData(d => ({ ...d, caseName: 'Johnso' })) },
      { delay: 80, action: () => setDemoData(d => ({ ...d, caseName: 'Johnson' })) },
      { delay: 80, action: () => setDemoData(d => ({ ...d, caseName: 'Johnson v' })) },
      { delay: 80, action: () => setDemoData(d => ({ ...d, caseName: 'Johnson v.' })) },
      { delay: 80, action: () => setDemoData(d => ({ ...d, caseName: 'Johnson v. ' })) },
      { delay: 80, action: () => setDemoData(d => ({ ...d, caseName: 'Johnson v. A' })) },
      { delay: 80, action: () => setDemoData(d => ({ ...d, caseName: 'Johnson v. AC' })) },
      { delay: 80, action: () => setDemoData(d => ({ ...d, caseName: 'Johnson v. ACM' })) },
      { delay: 80, action: () => setDemoData(d => ({ ...d, caseName: 'Johnson v. ACME' })) },
      { delay: 400, action: () => setDemoData(d => ({ ...d, caseNumber: '2026-CV-00847' })) },
      { delay: 400, action: () => setDemoData(d => ({ ...d, court: 'Los Angeles Superior Court' })) },
      { delay: 400, action: () => setDemoData(d => ({ ...d, caseValue: '$750,000' })) },
      // Step 2: Parties
      { delay: 800, action: () => setDemoStep(2) },
      { delay: 500, action: () => setDemoData(d => ({ ...d, plaintiff: 'Michael Johnson' })) },
      { delay: 400, action: () => setDemoData(d => ({ ...d, plaintiffType: 'Individual' })) },
      { delay: 400, action: () => setDemoData(d => ({ ...d, defendant: 'ACME Manufacturing Corp.' })) },
      { delay: 400, action: () => setDemoData(d => ({ ...d, defendantType: 'Corporation' })) },
      { delay: 400, action: () => setDemoData(d => ({ ...d, insurance: 'Hartford Insurance Co.' })) },
      { delay: 400, action: () => setDemoData(d => ({ ...d, policyLimit: '$2,000,000' })) },
      // Step 3: Legal Team & Court
      { delay: 800, action: () => setDemoStep(3) },
      { delay: 500, action: () => setDemoData(d => ({ ...d, judge: 'Hon. Patricia Chen' })) },
      { delay: 400, action: () => setDemoData(d => ({ ...d, judgeRating: '4.2/5 Plaintiff Favorable' })) },
      { delay: 400, action: () => setDemoData(d => ({ ...d, opposingCounsel: 'Smith & Associates LLP' })) },
      { delay: 400, action: () => setDemoData(d => ({ ...d, opposingAttorney: 'Robert Smith, Esq.' })) },
      { delay: 400, action: () => setDemoData(d => ({ ...d, opposingRecord: '67% Defense Win Rate' })) },
      // Step 4: Investigation Report
      { delay: 800, action: () => setDemoStep(4) },
      { delay: 400, action: () => setDemoData(d => ({ ...d, investigationStatus: 'Running background check...' })) },
      { delay: 600, action: () => setDemoData(d => ({ ...d, investigationStatus: 'Analyzing defendant history...' })) },
      { delay: 600, action: () => setDemoData(d => ({ ...d, investigationStatus: 'Checking litigation history...' })) },
      { delay: 600, action: () => setDemoData(d => ({ ...d, investigationStatus: 'Compiling report...' })) },
      { delay: 500, action: () => setDemoData(d => ({ ...d, 
        defendantLitHistory: '12 prior lawsuits',
        defendantSettlements: '8 settlements (avg $425K)',
        defendantVerdicts: '2 plaintiff verdicts',
        defendantRiskScore: 'HIGH RISK',
        plaintiffCredibility: '94% credibility score',
        plaintiffSocialMedia: 'Clean - No concerns'
      })) },
      // Step 5: Case Analysis & Settlement
      { delay: 1000, action: () => setDemoStep(5) },
      { delay: 400, action: () => setDemoData(d => ({ ...d, analysisStatus: 'Analyzing case factors...' })) },
      { delay: 600, action: () => setDemoData(d => ({ ...d, analysisStatus: 'Comparing similar cases...' })) },
      { delay: 600, action: () => setDemoData(d => ({ ...d, analysisStatus: 'Calculating settlement range...' })) },
      { delay: 500, action: () => setDemoData(d => ({ ...d, 
        winProbability: '72%',
        similarCases: '847',
        settlementLow: '$425,000',
        settlementHigh: '$675,000',
        recommendedDemand: '$825,000',
        confidenceScore: '94%'
      })) },
      // Step 6: Monitoring Setup
      { delay: 1000, action: () => setDemoStep(6) },
      { delay: 500, action: () => setDemoData(d => ({ ...d, 
        monitorDefendant: true,
        monitorPlaintiff: true,
        monitorCourt: true,
        monitorNews: true,
        alertsEnabled: true
      })) },
      // Step 7: Complete
      { delay: 1500, action: () => setDemoStep(7) },
    ];

    let timeouts = [];
    let totalDelay = 0;
    
    demoSequence.forEach(({ delay, action }) => {
      totalDelay += delay;
      timeouts.push(setTimeout(action, totalDelay));
    });

    return () => timeouts.forEach(t => clearTimeout(t));
  }, [showDemo]);

  const capabilities = [
    { title: 'Case Prediction', desc: 'AI-powered outcome analysis with multi-model verification', icon: Target, color: '#1D4ED8' },
    { title: 'Party Investigation', desc: 'Deep intelligence on opposing parties, counsel, and judges', icon: Search, color: '#DC2626' },
    { title: 'Document Intelligence', desc: 'Analyze contracts, extract key clauses, generate responses', icon: FileText, color: '#F59E0B' },
    { title: 'Settlement Modeling', desc: 'Data-driven settlement range analysis', icon: DollarSign, color: '#10B981' },
    { title: 'Service Networks', desc: '11 integrated networks: attorneys, experts, investigators, financing', icon: Users, color: '#8B5CF6' },
    { title: 'Litix Wizard', desc: 'Guided case onboarding from intake to resolution', icon: Sparkles, color: '#0EA5E9' },
  ];

  const serviceNetworks = [
    'Attorney Staffing', 'Expert Witnesses', 'Licensed Investigators', 
    'Insurance Analytics', 'Paralegal Network', 'Legal Courier',
    'Case Financing', 'E&O Coverage', 'Marketing Services'
  ];

  return (
    <main className="min-h-screen" data-testid="home-page">
      {/* Demo Modal */}
      {showDemo && (
        <div className="fixed inset-0 bg-black/70 z-[100] flex items-center justify-center p-4" data-testid="demo-modal">
          <div className="bg-white rounded-3xl w-full max-w-5xl max-h-[90vh] overflow-hidden shadow-2xl">
            {/* Demo Header */}
            <div className="p-5 border-b" style={{ background: 'linear-gradient(135deg, #1D4ED8, #3B82F6)' }}>
              <div className="flex justify-between items-center">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Sparkles className="h-5 w-5 text-white" />
                    <h2 className="text-xl font-bold text-white">Litix Wizard Demo</h2>
                  </div>
                  <p className="text-blue-100 text-sm">
                    Preview of ONE tool from our 40+ tool platform
                  </p>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setShowDemo(false)}
                  className="text-white hover:bg-white/20 rounded-full h-10 w-10 p-0"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </div>

            {/* Demo Content */}
            <div className="p-5 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 120px)' }}>
              {/* Progress Steps */}
              <div className="flex items-center justify-between mb-6 px-2 overflow-x-auto">
                {['Case Info', 'Parties', 'Court & Counsel', 'Investigation', 'Analysis', 'Monitoring', 'Complete'].map((step, i) => (
                  <div key={step} className="flex items-center flex-shrink-0">
                    <div className="flex flex-col items-center">
                      <div 
                        className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-500 ${
                          demoStep > i + 1 ? 'bg-emerald-500 text-white' : 
                          demoStep === i + 1 ? 'bg-blue-600 text-white ring-4 ring-blue-200' : 
                          'bg-slate-200 text-slate-500'
                        }`}
                      >
                        {demoStep > i + 1 ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
                      </div>
                      <span className={`text-[10px] mt-1 font-medium whitespace-nowrap ${demoStep >= i + 1 ? 'text-slate-800' : 'text-slate-400'}`}>
                        {step}
                      </span>
                    </div>
                    {i < 6 && (
                      <div 
                        className={`w-8 h-0.5 mx-1 rounded transition-all duration-500 ${
                          demoStep > i + 1 ? 'bg-emerald-500' : 'bg-slate-200'
                        }`}
                      />
                    )}
                  </div>
                ))}
              </div>

              {/* Demo Panels */}
              <div className="rounded-2xl border-2 p-5 min-h-[320px]" style={{ background: '#F8FAFC' }}>
                {/* Step 1: Case Info */}
                {demoStep <= 1 && (
                  <div className="space-y-4 animate-fade-in">
                    <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                      <FileText className="h-5 w-5 text-blue-600" />
                      Case Information
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs font-semibold text-slate-500 block mb-1">CASE NAME</label>
                        <div className="h-10 rounded-lg border-2 bg-white px-3 flex items-center font-medium text-sm" style={{ borderColor: demoData.caseName ? '#3B82F6' : '#E2E8F0' }}>
                          {demoData.caseName}
                          {demoStep <= 1 && demoData.caseName && !demoData.caseNumber && <span className="animate-pulse">|</span>}
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-slate-500 block mb-1">CASE NUMBER</label>
                        <div className={`h-10 rounded-lg border-2 bg-white px-3 flex items-center text-sm transition-all ${demoData.caseNumber ? 'border-blue-500 font-medium' : 'border-slate-200 text-slate-400'}`}>
                          {demoData.caseNumber || '—'}
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-slate-500 block mb-1">COURT</label>
                        <div className={`h-10 rounded-lg border-2 bg-white px-3 flex items-center text-sm transition-all ${demoData.court ? 'border-blue-500 font-medium' : 'border-slate-200 text-slate-400'}`}>
                          {demoData.court || '—'}
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-slate-500 block mb-1">ESTIMATED CASE VALUE</label>
                        <div className={`h-10 rounded-lg border-2 bg-white px-3 flex items-center text-sm transition-all ${demoData.caseValue ? 'border-emerald-500 font-bold text-emerald-700' : 'border-slate-200 text-slate-400'}`}>
                          {demoData.caseValue || '—'}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 2: Parties */}
                {demoStep === 2 && (
                  <div className="space-y-4 animate-fade-in">
                    <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                      <Users className="h-5 w-5 text-blue-600" />
                      Parties & Insurance
                    </h3>
                    <div className="grid grid-cols-2 gap-6">
                      <div className="p-4 rounded-xl bg-blue-50 border border-blue-200">
                        <p className="text-xs font-bold text-blue-600 mb-3">PLAINTIFF</p>
                        <div className="space-y-2">
                          <div className={`h-9 rounded-lg bg-white px-3 flex items-center text-sm transition-all ${demoData.plaintiff ? 'font-semibold text-slate-800' : 'text-slate-400'}`}>
                            {demoData.plaintiff || 'Loading...'}
                          </div>
                          <div className={`h-9 rounded-lg bg-white px-3 flex items-center text-sm ${demoData.plaintiffType ? 'text-slate-600' : 'text-slate-400'}`}>
                            Type: {demoData.plaintiffType || '—'}
                          </div>
                        </div>
                      </div>
                      <div className="p-4 rounded-xl bg-red-50 border border-red-200">
                        <p className="text-xs font-bold text-red-600 mb-3">DEFENDANT</p>
                        <div className="space-y-2">
                          <div className={`h-9 rounded-lg bg-white px-3 flex items-center text-sm transition-all ${demoData.defendant ? 'font-semibold text-slate-800' : 'text-slate-400'}`}>
                            {demoData.defendant || 'Loading...'}
                          </div>
                          <div className={`h-9 rounded-lg bg-white px-3 flex items-center text-sm ${demoData.defendantType ? 'text-slate-600' : 'text-slate-400'}`}>
                            Type: {demoData.defendantType || '—'}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="p-4 rounded-xl bg-amber-50 border border-amber-200">
                      <p className="text-xs font-bold text-amber-600 mb-3">INSURANCE COVERAGE</p>
                      <div className="grid grid-cols-2 gap-4">
                        <div className={`h-9 rounded-lg bg-white px-3 flex items-center text-sm ${demoData.insurance ? 'font-medium' : 'text-slate-400'}`}>
                          {demoData.insurance || 'Searching...'}
                        </div>
                        <div className={`h-9 rounded-lg bg-white px-3 flex items-center text-sm ${demoData.policyLimit ? 'font-bold text-emerald-700' : 'text-slate-400'}`}>
                          Policy Limit: {demoData.policyLimit || '—'}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 3: Court & Opposing Counsel */}
                {demoStep === 3 && (
                  <div className="space-y-4 animate-fade-in">
                    <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                      <Gavel className="h-5 w-5 text-blue-600" />
                      Court & Opposing Counsel Intelligence
                    </h3>
                    <div className="grid grid-cols-2 gap-6">
                      <div className="p-4 rounded-xl bg-purple-50 border border-purple-200">
                        <p className="text-xs font-bold text-purple-600 mb-3">ASSIGNED JUDGE</p>
                        <div className="space-y-2">
                          <div className={`h-9 rounded-lg bg-white px-3 flex items-center text-sm ${demoData.judge ? 'font-semibold text-slate-800' : 'text-slate-400'}`}>
                            {demoData.judge || 'Looking up...'}
                          </div>
                          {demoData.judgeRating && (
                            <div className="h-9 rounded-lg bg-emerald-100 px-3 flex items-center text-sm font-medium text-emerald-700">
                              {demoData.judgeRating}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="p-4 rounded-xl bg-slate-100 border border-slate-300">
                        <p className="text-xs font-bold text-slate-600 mb-3">OPPOSING COUNSEL</p>
                        <div className="space-y-2">
                          <div className={`h-9 rounded-lg bg-white px-3 flex items-center text-sm ${demoData.opposingCounsel ? 'font-semibold text-slate-800' : 'text-slate-400'}`}>
                            {demoData.opposingCounsel || 'Identifying...'}
                          </div>
                          <div className={`h-9 rounded-lg bg-white px-3 flex items-center text-sm ${demoData.opposingAttorney ? 'text-slate-600' : 'text-slate-400'}`}>
                            {demoData.opposingAttorney || '—'}
                          </div>
                          {demoData.opposingRecord && (
                            <div className="h-9 rounded-lg bg-amber-100 px-3 flex items-center text-sm font-medium text-amber-700">
                              {demoData.opposingRecord}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 4: Investigation Report */}
                {demoStep === 4 && (
                  <div className="space-y-4 animate-fade-in">
                    <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                      <Search className="h-5 w-5 text-blue-600" />
                      Investigative Intelligence Report
                    </h3>
                    {!demoData.defendantLitHistory ? (
                      <div className="flex items-center gap-3 p-4 rounded-xl bg-blue-50 border border-blue-200">
                        <Loader2 className="h-6 w-6 text-blue-600 animate-spin" />
                        <div>
                          <p className="font-semibold text-blue-800">{demoData.investigationStatus}</p>
                          <p className="text-sm text-blue-600">Compiling comprehensive party intelligence</p>
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-4">
                        <div className="p-4 rounded-xl bg-red-50 border border-red-200">
                          <p className="text-xs font-bold text-red-600 mb-3">DEFENDANT PROFILE</p>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between"><span className="text-slate-600">Litigation History:</span><span className="font-semibold">{demoData.defendantLitHistory}</span></div>
                            <div className="flex justify-between"><span className="text-slate-600">Prior Settlements:</span><span className="font-semibold">{demoData.defendantSettlements}</span></div>
                            <div className="flex justify-between"><span className="text-slate-600">Verdicts Against:</span><span className="font-semibold">{demoData.defendantVerdicts}</span></div>
                            <div className="mt-2 p-2 rounded-lg bg-red-200 text-center">
                              <span className="font-bold text-red-800">{demoData.defendantRiskScore}</span>
                            </div>
                          </div>
                        </div>
                        <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200">
                          <p className="text-xs font-bold text-emerald-600 mb-3">PLAINTIFF PROFILE</p>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between"><span className="text-slate-600">Credibility Score:</span><span className="font-semibold text-emerald-700">{demoData.plaintiffCredibility}</span></div>
                            <div className="flex justify-between"><span className="text-slate-600">Social Media Scan:</span><span className="font-semibold">{demoData.plaintiffSocialMedia}</span></div>
                            <div className="mt-4 p-2 rounded-lg bg-emerald-200 text-center">
                              <span className="font-bold text-emerald-800">STRONG CASE PROFILE</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Step 5: Analysis & Settlement */}
                {demoStep === 5 && (
                  <div className="space-y-4 animate-fade-in">
                    <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                      <Target className="h-5 w-5 text-blue-600" />
                      Case Analysis & Settlement Intelligence
                    </h3>
                    {!demoData.winProbability ? (
                      <div className="flex items-center gap-3 p-4 rounded-xl bg-blue-50 border border-blue-200">
                        <Loader2 className="h-6 w-6 text-blue-600 animate-spin" />
                        <div>
                          <p className="font-semibold text-blue-800">{demoData.analysisStatus}</p>
                          <p className="text-sm text-blue-600">Running proprietary analysis algorithms</p>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="p-5 rounded-2xl bg-gradient-to-r from-emerald-50 to-blue-50 border-2 border-emerald-200">
                          <div className="grid grid-cols-3 gap-4 text-center">
                            <div>
                              <p className="text-xs font-semibold text-slate-500 mb-1">WIN PROBABILITY</p>
                              <p className="text-3xl font-black text-blue-600">{demoData.winProbability}</p>
                            </div>
                            <div>
                              <p className="text-xs font-semibold text-slate-500 mb-1">SIMILAR CASES</p>
                              <p className="text-3xl font-black text-slate-700">{demoData.similarCases}</p>
                            </div>
                            <div>
                              <p className="text-xs font-semibold text-slate-500 mb-1">CONFIDENCE</p>
                              <p className="text-3xl font-black text-emerald-600">{demoData.confidenceScore}</p>
                            </div>
                          </div>
                        </div>
                        <div className="p-5 rounded-2xl bg-emerald-50 border-2 border-emerald-300 text-center">
                          <p className="text-xs font-bold text-emerald-600 mb-2">SETTLEMENT RANGE</p>
                          <p className="text-3xl font-black text-emerald-700">{demoData.settlementLow} — {demoData.settlementHigh}</p>
                          <p className="text-sm font-semibold text-emerald-600 mt-2">Recommended Opening Demand: {demoData.recommendedDemand}</p>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Step 6: Monitoring */}
                {demoStep === 6 && (
                  <div className="space-y-4 animate-fade-in">
                    <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                      <Bell className="h-5 w-5 text-blue-600" />
                      Ongoing Monitoring & Alerts
                    </h3>
                    <p className="text-slate-600">Set up real-time monitoring for all parties and legal developments</p>
                    <div className="grid grid-cols-2 gap-4">
                      {[
                        { label: 'Defendant Activity Monitoring', desc: 'New lawsuits, filings, business changes', enabled: demoData.monitorDefendant },
                        { label: 'Plaintiff Profile Monitoring', desc: 'Social media, public records, credibility', enabled: demoData.monitorPlaintiff },
                        { label: 'Court Docket Monitoring', desc: 'Filings, deadlines, scheduling orders', enabled: demoData.monitorCourt },
                        { label: 'News & Legal Updates', desc: 'Related cases, regulatory changes', enabled: demoData.monitorNews },
                      ].map(item => (
                        <div key={item.label} className={`p-4 rounded-xl border-2 transition-all ${item.enabled ? 'bg-emerald-50 border-emerald-300' : 'bg-slate-50 border-slate-200'}`}>
                          <div className="flex items-center gap-3 mb-2">
                            {item.enabled ? <CheckCircle2 className="h-5 w-5 text-emerald-600" /> : <div className="h-5 w-5 rounded-full border-2 border-slate-300" />}
                            <span className="font-semibold text-slate-800">{item.label}</span>
                          </div>
                          <p className="text-xs text-slate-500 ml-8">{item.desc}</p>
                        </div>
                      ))}
                    </div>
                    {demoData.alertsEnabled && (
                      <div className="p-4 rounded-xl bg-blue-50 border border-blue-200 flex items-center gap-3">
                        <BellRing className="h-6 w-6 text-blue-600" />
                        <div>
                          <p className="font-semibold text-blue-800">Real-time Alerts Enabled</p>
                          <p className="text-sm text-blue-600">You'll receive instant notifications on any critical updates</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Step 7: Complete */}
                {demoStep === 7 && (
                  <div className="text-center py-6 animate-fade-in">
                    <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                      <CheckCircle2 className="h-10 w-10 text-emerald-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-slate-800 mb-2">Case Setup Complete!</h3>
                    <p className="text-slate-600 mb-6 max-w-lg mx-auto">
                      This was just the <strong>Litix Wizard</strong> — one tool from our comprehensive platform. 
                      The full system includes 40+ intelligence tools, 11 service networks, document generation, 
                      and complete case management.
                    </p>
                    <div className="flex gap-4 justify-center">
                      <Button 
                        onClick={() => { setShowDemo(false); navigate('/register'); }}
                        className="rounded-xl h-12 px-6 font-bold text-white"
                        style={{ background: 'linear-gradient(135deg,#1D4ED8,#3B82F6)' }}
                      >
                        Request Full Access
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={() => { setDemoStep(0); setDemoData({}); }}
                        className="rounded-xl h-12 px-6 font-bold"
                      >
                        Replay Demo
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Demo Disclaimer */}
              <div className="mt-4 p-3 rounded-xl bg-amber-50 border border-amber-200">
                <div className="flex items-start gap-3">
                  <Info className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-amber-800 text-sm">Limited Preview Demo</p>
                    <p className="text-xs text-amber-700">
                      This demonstrates a fraction of Litix Wizard capabilities. The full platform includes evidence management, 
                      document generation, expert witness matching, legal financing, and much more across 40+ integrated tools.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sticky Header */}
      <div className="sticky top-0 z-50 py-2" style={{ background: 'linear-gradient(135deg, #1D4ED8, #3B82F6)' }}>
        <div className="container-shell flex items-center justify-center gap-4 text-white text-sm">
          <span className="font-bold">Limited Availability: $499/mo (Promotional Rate)</span>
          <Button onClick={() => navigate('/register')} size="sm" className="rounded-full px-4 h-8 font-bold text-blue-600 bg-white hover:bg-blue-50">
            Request Access
          </Button>
        </div>
      </div>

      {/* Navigation */}
      <header className="py-4" style={{ background: '#fff', borderBottom: '1px solid #E2E8F0' }}>
        <div className="container-shell flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <TrialezeLogo size="default" showText />
          </Link>
          <nav className="hidden md:flex items-center gap-6 text-sm" style={{ color: '#334155' }}>
            <Link to="/features" className="hover:text-blue-700">All Tools</Link>
            <Link to="/pricing" className="hover:text-blue-700">Pricing</Link>
            <Link to="/solutions" className="hover:text-blue-700">Solutions</Link>
            <Link to="/contact" className="hover:text-blue-700">Contact</Link>
          </nav>
          <div className="flex items-center gap-4">
            {user ? (
              <Button onClick={() => navigate('/platform/dashboard')} className="rounded-xl h-11 px-6 font-bold text-white" style={{ background: 'linear-gradient(135deg,#1D4ED8,#3B82F6)' }}>
                Go to Dashboard
              </Button>
            ) : (
              <>
                <Button variant="ghost" onClick={() => navigate('/login')} className="font-semibold">Sign In</Button>
                <Button onClick={() => navigate('/register')} className="rounded-xl h-11 px-6 font-bold text-white" style={{ background: 'linear-gradient(135deg,#1D4ED8,#3B82F6)' }}>
                  Request Access
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 relative overflow-hidden" style={{ background: 'linear-gradient(180deg, #f0f7ff 0%, #ffffff 100%)' }}>
        <div className="container-shell">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold mb-6" style={{ background: '#EFF6FF', color: '#1D4ED8' }}>
              <Scale className="h-4 w-4" />
              Invitation-Only Legal Intelligence
            </div>
            
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black leading-none mb-6" style={{ color: '#0F172A', letterSpacing: '-0.04em' }}>
              Legal Intelligence<br />
              <span style={{ background: 'linear-gradient(135deg, #1D4ED8, #14B8A6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                That Wins Cases
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto" style={{ color: '#475569' }}>
              Predict outcomes. Investigate parties. Generate documents. Access 11 service networks. 
              <strong> Everything an attorney needs to dominate.</strong>
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Button onClick={() => navigate('/register')} className="rounded-xl h-14 px-8 text-lg font-bold text-white" style={{ background: 'linear-gradient(135deg,#1D4ED8,#3B82F6)', boxShadow: '0 20px 40px rgba(29,78,216,.3)' }}>
                Request Membership
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button variant="outline" onClick={() => setShowDemo(true)} className="rounded-xl h-14 px-8 text-lg font-bold border-2" data-testid="watch-demo-btn">
                <Eye className="mr-2 h-5 w-5" />
                Watch Demo
              </Button>
            </div>
            
            <p className="text-sm" style={{ color: '#64748B' }}>
              $599 activation • $499/month membership • Cancel anytime
            </p>
          </div>
        </div>
      </section>

      {/* Platform Stats */}
      <section className="py-12" style={{ background: '#fff', borderTop: '1px solid #E2E8F0', borderBottom: '1px solid #E2E8F0' }}>
        <div className="container-shell">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl font-black" style={{ color: '#1D4ED8' }}>40+</div>
              <div className="text-sm" style={{ color: '#64748B' }}>Intelligence Tools</div>
            </div>
            <div>
              <div className="text-4xl font-black" style={{ color: '#1D4ED8' }}>11</div>
              <div className="text-sm" style={{ color: '#64748B' }}>Service Networks</div>
            </div>
            <div>
              <div className="text-4xl font-black" style={{ color: '#1D4ED8' }}>135+</div>
              <div className="text-sm" style={{ color: '#64748B' }}>Torts Covered</div>
            </div>
            <div>
              <div className="text-4xl font-black" style={{ color: '#1D4ED8' }}>50</div>
              <div className="text-sm" style={{ color: '#64748B' }}>States Supported</div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Capabilities */}
      <section className="py-20" style={{ background: '#0F172A' }}>
        <div className="container-shell">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">
              Complete Legal Operating System
            </h2>
            <p style={{ color: '#94A3B8' }}>
              Everything you need to evaluate, strategize, and execute.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {capabilities.map(({ title, desc, icon: Icon, color }) => (
              <div key={title} className="rounded-2xl p-6" style={{ background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.1)' }}>
                <div className="w-12 h-12 rounded-xl mb-4 flex items-center justify-center" style={{ background: color }}>
                  <Icon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{title}</h3>
                <p className="text-sm" style={{ color: '#94A3B8' }}>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Litix Wizard Feature */}
      <section className="py-20" style={{ background: 'linear-gradient(180deg, #f8fafc 0%, #fff 100%)' }}>
        <div className="container-shell">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold mb-4" style={{ background: '#DBEAFE', color: '#1D4ED8' }}>
                <Sparkles className="h-4 w-4" />
                Litix Wizard
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: '#0F172A' }}>
                From Intake to Resolution
              </h2>
              <p className="text-lg mb-6" style={{ color: '#64748B' }}>
                The Litix Wizard guides you through every step — case setup, evidence collection, party investigation, AI analysis, and service requests. 135+ torts pre-loaded. Nothing falls through the cracks.
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  'Complete case setup with all party details',
                  'Document and evidence collection',
                  'Automatic AI-powered case analysis',
                  'Settlement range estimation',
                  'One-click service network requests',
                  'Ongoing case monitoring'
                ].map(item => (
                  <li key={item} className="flex items-center gap-3">
                    <CheckCircle2 className="h-5 w-5 flex-shrink-0" style={{ color: '#10B981' }} />
                    <span style={{ color: '#334155' }}>{item}</span>
                  </li>
                ))}
              </ul>
              <Button onClick={() => navigate('/tools/litix-wizard')} className="rounded-xl h-12 px-6 font-bold text-white" style={{ background: 'linear-gradient(135deg,#1D4ED8,#3B82F6)' }}>
                Launch Wizard
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
            <div className="rounded-3xl p-6" style={{ background: '#fff', border: '1px solid #E2E8F0', boxShadow: '0 20px 40px rgba(0,0,0,.08)' }}>
              <div className="space-y-4">
                {['Case Setup', 'Evidence Upload', 'Party Analysis', 'AI Report', 'Service Requests', 'Monitoring'].map((step, i) => (
                  <div key={step} className="flex items-center gap-4 p-3 rounded-xl" style={{ background: i === 0 ? '#EFF6FF' : '#F8FAFC' }}>
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold" style={{ background: '#1D4ED8', color: '#fff' }}>
                      {i + 1}
                    </div>
                    <span className="font-medium" style={{ color: '#334155' }}>{step}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Networks */}
      <section className="py-20" style={{ background: '#fff' }}>
        <div className="container-shell">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: '#0F172A' }}>
              11 Integrated Service Networks
            </h2>
            <p className="text-lg" style={{ color: '#64748B' }}>
              Access nationwide networks of professionals — all through one platform.
            </p>
          </div>
          
          <div className="flex flex-wrap justify-center gap-3 mb-8">
            {serviceNetworks.map(network => (
              <div key={network} className="px-4 py-2 rounded-full text-sm font-medium" style={{ background: '#F1F5F9', color: '#334155' }}>
                {network}
              </div>
            ))}
          </div>
          
          <div className="text-center">
            <Button onClick={() => navigate('/tools/services-hub')} variant="outline" className="rounded-xl h-12 px-6 font-bold">
              Explore Services Hub
            </Button>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20" style={{ background: '#0F172A' }}>
        <div className="container-shell">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">
              Membership
            </h2>
            <p style={{ color: '#94A3B8' }}>
              Invitation-only access. Limited availability.
            </p>
          </div>
          
          <div className="max-w-lg mx-auto">
            <div className="rounded-3xl p-8" style={{ background: '#fff' }}>
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold mb-4" style={{ color: '#0F172A' }}>Professional Membership</h3>
                <div className="mb-2">
                  <span className="text-sm" style={{ color: '#64748B' }}>Activation Fee: </span>
                  <span className="font-bold" style={{ color: '#0F172A' }}>$599</span>
                  <span className="text-sm" style={{ color: '#64748B' }}> one-time</span>
                </div>
                <div className="flex items-baseline justify-center gap-2">
                  <span className="text-5xl font-black" style={{ color: '#0F172A' }}>$499</span>
                  <span style={{ color: '#64748B' }}>/month</span>
                </div>
                <p className="text-sm mt-2" style={{ color: '#64748B' }}>
                  Promotional rate — pricing subject to change
                </p>
              </div>
              
              <div className="space-y-3 mb-8">
                {[
                  'Full platform access (40+ tools)',
                  'All 11 service networks',
                  'Litix Wizard with 135+ torts',
                  'Multi-model AI verification',
                  'Party investigation reports',
                  'Document generation',
                  'Settlement modeling',
                  'Dedicated support'
                ].map(item => (
                  <div key={item} className="flex items-center gap-3">
                    <CheckCircle2 className="h-5 w-5 flex-shrink-0" style={{ color: '#10B981' }} />
                    <span style={{ color: '#334155' }}>{item}</span>
                  </div>
                ))}
              </div>
              
              <Button onClick={() => navigate('/register')} className="w-full rounded-xl h-14 text-lg font-bold text-white" style={{ background: 'linear-gradient(135deg,#1D4ED8,#3B82F6)' }}>
                Request Membership
              </Button>
              
              <p className="text-center text-sm mt-4" style={{ color: '#64748B' }}>
                Cancel anytime • No long-term contracts
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20" style={{ background: 'linear-gradient(135deg, #1D4ED8, #0F766E)' }}>
        <div className="container-shell text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">
            Ready to Transform Your Practice?
          </h2>
          <p className="text-lg mb-8 max-w-2xl mx-auto" style={{ color: 'rgba(255,255,255,.8)' }}>
            Trialeze is built for attorneys who demand excellence. Request access to join.
          </p>
          <Button onClick={() => navigate('/register')} className="rounded-xl h-14 px-8 text-lg font-bold bg-white text-blue-700 hover:bg-blue-50">
            Request Membership
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12" style={{ background: '#0F172A', borderTop: '1px solid rgba(255,255,255,.1)' }}>
        <div className="container-shell">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <TrialezeLogo size="small" showText />
              <p className="text-sm mt-4" style={{ color: '#64748B' }}>
                Legal intelligence for attorneys who demand excellence.
              </p>
            </div>
            <div>
              <h4 className="font-bold mb-4 text-white">Platform</h4>
              <div className="space-y-2 text-sm" style={{ color: '#94A3B8' }}>
                <Link to="/tools" className="block hover:text-white">All Tools</Link>
                <Link to="/tools/investigator" className="block hover:text-white">The Investigator</Link>
                <Link to="/tools/litix-wizard" className="block hover:text-white">Litix Wizard</Link>
                <Link to="/tools/services-hub" className="block hover:text-white">Services Hub</Link>
              </div>
            </div>
            <div>
              <h4 className="font-bold mb-4 text-white">Company</h4>
              <div className="space-y-2 text-sm" style={{ color: '#94A3B8' }}>
                <Link to="/about" className="block hover:text-white">About</Link>
                <Link to="/pricing" className="block hover:text-white">Membership</Link>
                <Link to="/contact" className="block hover:text-white">Contact</Link>
              </div>
            </div>
            <div>
              <h4 className="font-bold mb-4 text-white">Contact</h4>
              <div className="space-y-2 text-sm" style={{ color: '#94A3B8' }}>
                <p>864 Grand Ave Suite 408</p>
                <p>San Diego, CA 92109</p>
                <p className="font-bold text-white">510-422-4451</p>
              </div>
            </div>
          </div>
          <div className="pt-8 flex flex-col md:flex-row justify-between items-center gap-4" style={{ borderTop: '1px solid rgba(255,255,255,.1)' }}>
            <p className="text-sm" style={{ color: '#64748B' }}>
              © 2026 Trialeze. All rights reserved.
            </p>
            <div className="flex gap-6 text-sm" style={{ color: '#64748B' }}>
              <Link to="/terms" className="hover:text-white">Terms</Link>
              <Link to="/privacy" className="hover:text-white">Privacy</Link>
            </div>
          </div>
        </div>
      </footer>
    </main>
  );
};

export default HomePage;
